/* =========================================================
   AF — Recently Viewed (Homepage Slider)
   File: assets/af-recently-viewed.js

   - Tracks product handles on product pages (localStorage)
   - Renders a horizontal scroller into: [data-af-recently-viewed]
   - Desktop arrow nav grouped together at BOTTOM-LEFT (single pill group)
   - Updates progress bar on scroll
   - Clear history confirm modal (B/W)
   - Hides section when empty (new user / after clearing)
   - NO LOOP

   Desktop upgrade:
   - Mouse grab + drag to scroll + inertia
   - NO SNAP on desktop (free scroll)
   - Mobile/touch snap remains native
   - ✅ FIX: Cards remain CLICKABLE on desktop
     (we DO NOT set .is-dragging or preventDefault until user actually drags)
   ========================================================= */

(() => {
  const KEY = "af_recently_viewed_handles";
  const MAX_DEFAULT = 24;

  const isDesktop = () =>
    window.matchMedia &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const isProductPage = () => {
    const cls =
      (document.documentElement.className || "") +
      " " +
      (document.body?.className || "");
    return (
      cls.includes("template-product") ||
      window.location.pathname.startsWith("/products/")
    );
  };

  const getHandleFromPath = () => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    const i = parts.indexOf("products");
    return i >= 0 && parts[i + 1] ? decodeURIComponent(parts[i + 1]) : "";
  };

  const readList = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || "[]");
      return Array.isArray(v) ? v.filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const writeList = (arr) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(arr));
    } catch {}
  };

  const hideWholeSection = (root) => {
    const section = root.closest(".af-rvw") || root.closest("section");
    if (section) section.style.display = "none";
    root.style.display = "none";
  };

  const escapeHtml = (s) =>
    String(s || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const sizedUrl = (url, w) => {
    if (!url) return "";
    return url + (url.includes("?") ? "&" : "?") + "width=" + w;
  };

  const track = () => {
    if (!isProductPage()) return;
    const handle = getHandleFromPath();
    if (!handle) return;

    let list = readList();
    list = list.filter((h) => h !== handle);
    list.unshift(handle);
    if (list.length > MAX_DEFAULT) list = list.slice(0, MAX_DEFAULT);
    writeList(list);
  };

  const fetchProduct = async (handle) => {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error("product fetch failed");
    return await res.json();
  };

  const renderCard = (p) => {
    const url = p.url || `/products/${p.handle}`;
    const img = p.featured_image || "";
    const vendor = p.vendor || "";

    return `
      <li class="af-rvw__slide" role="listitem">
        <div class="af-rvw-card">
          <a class="af-rvw-card__media" href="${url}" aria-label="${escapeHtml(
      p.title
    )}">
            <div class="af-rvw-card__frame">
              <img
                src="${sizedUrl(img, 720)}"
                srcset="${sizedUrl(img, 360)} 360w, ${sizedUrl(img, 540)} 540w, ${sizedUrl(img, 720)} 720w"
                sizes="(min-width: 990px) 320px, 70vw"
                alt="${escapeHtml(p.title)}"
                loading="lazy"
                width="720"
                height="720"
                draggable="false"
              />
            </div>
          </a>

          <div class="af-rvw-card__meta">
            ${
              vendor
                ? `<div class="af-rvw-card__vendor">${escapeHtml(vendor)}</div>`
                : ""
            }
            <div class="af-rvw-card__title">
              <a href="${url}">${escapeHtml(p.title)}</a>
            </div>
          </div>
        </div>
      </li>
    `;
  };

  const openModal = (root) => {
    const overlay = root.querySelector(".af-rvw-modal");
    if (!overlay) return;
    overlay.classList.add("is-open");
    document.documentElement.classList.add("af-rvw-modal-open");
    overlay.setAttribute("aria-hidden", "false");
    (
      overlay.querySelector(".af-rvw-modal__confirm") ||
      overlay.querySelector("button")
    )?.focus?.();
  };

  const closeModal = (root) => {
    const overlay = root.querySelector(".af-rvw-modal");
    if (!overlay) return;
    overlay.classList.remove("is-open");
    document.documentElement.classList.remove("af-rvw-modal-open");
    overlay.setAttribute("aria-hidden", "true");
  };

  const setProgress = (trackEl, fill) => {
    if (!trackEl || !fill) return;
    const max = trackEl.scrollWidth - trackEl.clientWidth;
    const p = max <= 0 ? 0 : Math.max(0, Math.min(1, trackEl.scrollLeft / max));
    fill.style.transform = `scaleX(${p})`;
  };

  const updateNavDisabled = (trackEl, prevBtn, nextBtn) => {
    if (!trackEl || !prevBtn || !nextBtn) return;
    const max = trackEl.scrollWidth - trackEl.clientWidth;
    const x = trackEl.scrollLeft;

    const atStart = x <= 1;
    const atEnd = max <= 1 ? true : x >= max - 1;

    prevBtn.disabled = atStart;
    nextBtn.disabled = atEnd;
    prevBtn.classList.toggle("is-disabled", atStart);
    nextBtn.classList.toggle("is-disabled", atEnd);
  };

  const getStep = (trackEl) => {
    const first = trackEl?.querySelector(".af-rvw__slide");
    if (!first) return Math.round(trackEl.clientWidth * 0.85);
    const rect = first.getBoundingClientRect();
    const styles = window.getComputedStyle(trackEl);
    const gap = parseFloat(styles.columnGap || styles.gap || "26") || 26;
    return Math.round(rect.width + gap);
  };

  const mount = async (root) => {
    const max =
      parseInt(root.getAttribute("data-max") || `${MAX_DEFAULT}`, 10) ||
      MAX_DEFAULT;
    const showClear = (root.getAttribute("data-clear") || "true") !== "false";
    const heading = root.getAttribute("data-heading") || "Decants you looked at";

    // Skip the product being viewed: track() has just put it first.
    const current = getHandleFromPath();
    const handles = readList().filter((h) => h !== current).slice(0, max);
    if (!handles.length) {
      hideWholeSection(root);
      return;
    }

    const skeletonCount = Math.min(handles.length, 6);
    const skels = Array.from({ length: skeletonCount })
      .map(
        () => `
        <li class="af-rvw__slide" role="listitem">
          <div class="af-rvw-card af-rvw-card--skeleton">
            <div class="af-rvw-card__frame">
              <div class="af-rvw-skel af-rvw-skel--img"></div>
            </div>
            <div class="af-rvw-card__meta">
              <div class="af-rvw-skel af-rvw-skel--vendor"></div>
              <div class="af-rvw-skel af-rvw-skel--title"></div>
            </div>
          </div>
        </li>
      `
      )
      .join("");

    root.innerHTML = `
      <div class="af-rvw__head">
        <h2 class="af-rvw__title">${escapeHtml(heading)}</h2>

        <div class="af-rvw__progress" aria-hidden="true">
          <span class="af-rvw__progress-fill"></span>
        </div>

        <div class="af-rvw__head-actions">
          ${
            showClear
              ? `<button type="button" class="af-rvw__clear">Clear history</button>`
              : ""
          }
        </div>
      </div>

      <div class="af-rvw__track">
        <ul class="af-rvw__slider" role="list" aria-label="Recently viewed">${skels}</ul>

        <div class="af-rvw__nav" aria-label="Carousel navigation">
          <button type="button" class="af-rvw__nav-btn af-rvw__nav-btn--prev" aria-label="Previous" disabled>
            <span class="af-rvw__nav-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M14.5 5.5L8 12l6.5 6.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>

          <button type="button" class="af-rvw__nav-btn af-rvw__nav-btn--next" aria-label="Next">
            <span class="af-rvw__nav-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9.5 5.5L16 12l-6.5 6.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </div>

      <div class="af-rvw-modal" aria-hidden="true">
        <div class="af-rvw-modal__overlay" data-af-rvw-close></div>
        <div class="af-rvw-modal__panel" role="dialog" aria-modal="true" aria-label="Clear browsing history">
          <button type="button" class="af-rvw-modal__x" aria-label="Close" data-af-rvw-close>×</button>

          <div class="af-rvw-modal__icon" aria-hidden="true">!</div>
          <div class="af-rvw-modal__title">Clear Browsing History</div>
          <div class="af-rvw-modal__desc">
            Remove all <strong class="af-rvw-modal__count">${handles.length}</strong> recently viewed decants? This action cannot be undone.
          </div>

          <div class="af-rvw-modal__actions">
            <button type="button" class="af-rvw-modal__btn af-rvw-modal__cancel" data-af-rvw-close>Cancel</button>
            <button type="button" class="af-rvw-modal__btn af-rvw-modal__confirm">Clear History</button>
          </div>
        </div>
      </div>
    `;

    const trackEl = root.querySelector(".af-rvw__slider");
    const progressFill = root.querySelector(".af-rvw__progress-fill");
    const prevBtn = root.querySelector(".af-rvw__nav-btn--prev");
    const nextBtn = root.querySelector(".af-rvw__nav-btn--next");

    const results = await Promise.allSettled(handles.map((h) => fetchProduct(h)));
    const products = results
      .filter((r) => r.status === "fulfilled" && r.value)
      .map((r) => r.value);

    if (!products.length) {
      hideWholeSection(root);
      return;
    }

    trackEl.innerHTML = products.map(renderCard).join("");

    // ===== UI updates (throttled to 1/frame) =====
    let uiRaf = 0;
    const scheduleUI = () => {
      if (uiRaf) return;
      uiRaf = requestAnimationFrame(() => {
        uiRaf = 0;
        setProgress(trackEl, progressFill);
        updateNavDisabled(trackEl, prevBtn, nextBtn);
      });
    };

    scheduleUI();
    trackEl.addEventListener("scroll", scheduleUI, { passive: true });

    const scrollByStep = (dir) => {
      const step = getStep(trackEl);
      trackEl.scrollBy({ left: dir * step, behavior: "smooth" });
    };
    prevBtn?.addEventListener("click", () => scrollByStep(-1));
    nextBtn?.addEventListener("click", () => scrollByStep(1));

    const ro = new ResizeObserver(scheduleUI);
    ro.observe(trackEl);

    // =========================================================
    // Desktop: grab + drag + inertia (CLICKABLE FIX)
    // =========================================================

    // hard-disable snap inline on desktop (extra safety)
    if (isDesktop()) trackEl.style.scrollSnapType = "none";

    // stop native browser drag (images/links)
    trackEl.querySelectorAll("img, a").forEach((el) => el.setAttribute("draggable", "false"));
    trackEl.addEventListener("dragstart", (e) => e.preventDefault(), true);

    // avoid double-binding
    if (!trackEl.dataset.afRvwDragInit) {
      trackEl.dataset.afRvwDragInit = "2";

      let isDown = false;
      let dragged = false;

      let startX = 0;
      let startScrollLeft = 0;

      let lastX = 0;
      let lastT = 0;
      let vx = 0; // px/ms

      let inertiaRaf = 0;

      const DRAG_THRESHOLD = 7;

      const shouldIgnoreDragStart = (el) =>
        !!el.closest(".af-rvw__nav, .af-rvw__clear, .af-rvw-modal, [data-af-no-drag]");

      const cancelInertia = () => {
        if (inertiaRaf) {
          cancelAnimationFrame(inertiaRaf);
          inertiaRaf = 0;
        }
        trackEl.classList.remove("is-dragging");
      };

      const startInertia = () => {
        let v = -vx * 12;

        const MAX_V = 1.6; // px/ms
        v = Math.max(-MAX_V, Math.min(MAX_V, v));

        const friction = 0.86;
        let last = performance.now();

        const step = (t) => {
          const dt = Math.min(32, t - last);
          last = t;

          trackEl.scrollLeft += v * dt;
          v *= Math.pow(friction, dt / 16);

          if (Math.abs(v) > 0.25) {
            inertiaRaf = requestAnimationFrame(step);
          } else {
            inertiaRaf = 0;
            trackEl.classList.remove("is-dragging");
          }
        };

        inertiaRaf = requestAnimationFrame(step);
      };

      const onDown = (e) => {
        if (!isDesktop()) return;
        if (e.button !== 0) return;
        if (shouldIgnoreDragStart(e.target)) return;

        // ✅ DO NOT preventDefault here (keeps normal clicks healthy)
        cancelInertia();

        isDown = true;
        dragged = false;

        startX = e.clientX;
        startScrollLeft = trackEl.scrollLeft;

        lastX = e.clientX;
        lastT = performance.now();
        vx = 0;

        // ✅ DO NOT set is-dragging yet — only after threshold
      };

      const onMove = (e) => {
        if (!isDown) return;

        const dx = e.clientX - startX;

        if (!dragged) {
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          dragged = true;
          trackEl.classList.add("is-dragging");
        }

        // ✅ preventDefault only AFTER real drag starts
        e.preventDefault();
        trackEl.scrollLeft = startScrollLeft - dx;

        const now = performance.now();
        const dt = Math.max(1, now - lastT);
        vx = (e.clientX - lastX) / dt;

        lastX = e.clientX;
        lastT = now;
      };

      const onUp = () => {
        if (!isDown) return;
        isDown = false;

        if (dragged) {
          // cancel the click that follows a drag
          const cancelClick = (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            trackEl.removeEventListener("click", cancelClick, true);
          };
          trackEl.addEventListener("click", cancelClick, true);

          startInertia();
        } else {
          trackEl.classList.remove("is-dragging");
        }

        dragged = false;
      };

      // Scoped listeners: only attach window move/up while dragging
      const attachWindowListeners = () => {
        window.addEventListener("mousemove", onMove, { passive: false });
        window.addEventListener("mouseup", onUpAndClean, true);
      };

      const removeWindowListeners = () => {
        window.removeEventListener("mousemove", onMove, { passive: false });
        window.removeEventListener("mouseup", onUpAndClean, true);
      };

      const onUpAndClean = () => {
        onUp();
        removeWindowListeners();
      };

      const onDownScoped = (e) => {
        onDown(e);
        if (isDown) attachWindowListeners();
      };

      trackEl.addEventListener("mousedown", onDownScoped, true);
    }

    // Clear handlers
    const clearBtn = root.querySelector(".af-rvw__clear");
    if (clearBtn) clearBtn.addEventListener("click", () => openModal(root));

    root.querySelectorAll("[data-af-rvw-close]").forEach((el) => {
      el.addEventListener("click", () => closeModal(root));
    });

    const confirm = root.querySelector(".af-rvw-modal__confirm");
    if (confirm) {
      confirm.addEventListener("click", () => {
        writeList([]);
        closeModal(root);
        hideWholeSection(root);
      });
    }

    // ESC closes
    root.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeModal(root);
    });
  };

  const boot = () => {
    track();
    document.querySelectorAll("[data-af-recently-viewed]").forEach((r) => mount(r));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
